import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { BadgeData, getBadges, saveBadge, updateBadge, deleteBadge, checkBadgeKeyExists, uploadBadgeImage } from '@/lib/firebase';
import { useAuth } from '@/contexts/auth-context';
import { Award, Plus, Edit, Trash2, ArrowUpDown } from 'lucide-react';
import { ImageUpload } from '@/components/image-upload';

const COLOUR_OPTIONS = ['gold', 'silver', 'bronze', 'green', 'purple', 'blue', 'pink', 'yellow'] as const;
const TYPE_OPTIONS = ['program', 'steps', 'streak', 'yoga', 'community', 'content'] as const;

const getColourClasses = (colour: BadgeData['colour']) => {
  const colourMap = {
    gold: 'bg-yellow-400 text-yellow-900',
    silver: 'bg-slate-300 text-slate-700',
    bronze: 'bg-orange-600 text-orange-100',
    green: 'bg-green-500 text-green-100',
    purple: 'bg-purple-500 text-purple-100',
    blue: 'bg-blue-500 text-blue-100',
    pink: 'bg-pink-500 text-pink-100',
    yellow: 'bg-yellow-500 text-yellow-100'
  };
  return colourMap[colour] || colourMap.gold;
};

export default function BadgesPage() {
  const [badges, setBadges] = useState<BadgeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBadge, setEditingBadge] = useState<BadgeData | null>(null);
  const [formData, setFormData] = useState<Omit<BadgeData, 'id'>>({
    name: '',
    description: '',
    messageOnEarning: '',
    key: '',
    colour: 'gold',
    type: 'program',
    imageUrl: '',
    enabled: true
  });
  const [saving, setSaving] = useState(false);
  const [keyError, setKeyError] = useState('');
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      loadBadges();
    }
  }, [user]);

  const loadBadges = async () => {
    try {
      setLoading(true);
      const badgesData = await getBadges();
      setBadges(badgesData);
    } catch (error: any) {
      toast({
        title: "Failed to load badges",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddBadge = () => {
    setEditingBadge(null);
    setFormData({
      name: '',
      description: '',
      messageOnEarning: '',
      key: '',
      colour: 'gold',
      type: 'program',
      imageUrl: '',
      enabled: true
    });
    setKeyError('');
    setModalOpen(true);
  };

  const handleEditBadge = (badge: BadgeData) => {
    setEditingBadge(badge);
    setFormData({
      name: badge.name,
      description: badge.description,
      messageOnEarning: badge.messageOnEarning || '',
      key: badge.key,
      colour: badge.colour,
      type: badge.type,
      imageUrl: badge.imageUrl,
      enabled: badge.enabled
    });
    setKeyError('');
    setModalOpen(true);
  };

  const validateKey = async (key: string): Promise<boolean> => {
    if (!key.trim()) {
      setKeyError('Key is required');
      return false;
    }

    // Check if key contains only valid characters (lowercase letters, numbers, hyphens, underscores)
    const keyRegex = /^[a-z0-9_-]+$/;
    if (!keyRegex.test(key)) {
      setKeyError('Key can only contain lowercase letters, numbers, hyphens, and underscores');
      return false;
    }

    // Check if key already exists
    const keyExists = await checkBadgeKeyExists(key, editingBadge?.id);
    if (keyExists) {
      setKeyError('This key already exists');
      return false;
    }

    setKeyError('');
    return true;
  };

  const handleSaveBadge = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Name required",
        description: "Please enter the badge name.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.messageOnEarning.trim()) {
      toast({
        title: "Message on earning required",
        description: "Please enter the message shown when this badge is earned.",
        variant: "destructive",
      });
      return;
    }

    // Validate key
    const isKeyValid = await validateKey(formData.key);
    if (!isKeyValid) {
      return;
    }

    try {
      setSaving(true);
      
      if (editingBadge) {
        await updateBadge(editingBadge.id!, formData);
        toast({
          title: "Badge updated",
          description: `${formData.name} has been updated successfully.`,
        });
      } else {
        await saveBadge(formData);
        toast({
          title: "Badge created",
          description: `${formData.name} has been added successfully.`,
        });
      }
      
      setModalOpen(false);
      loadBadges();
    } catch (error: any) {
      toast({
        title: editingBadge ? "Failed to update badge" : "Failed to create badge",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBadge = async (badge: BadgeData) => {
    if (!confirm(`Are you sure you want to delete ${badge.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteBadge(badge.id!);
      toast({
        title: "Badge deleted",
        description: `${badge.name} has been deleted successfully.`,
      });
      loadBadges();
    } catch (error: any) {
      toast({
        title: "Failed to delete badge",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleToggleEnabled = async (badge: BadgeData) => {
    try {
      await updateBadge(badge.id!, { enabled: !badge.enabled });
      toast({
        title: "Badge updated",
        description: `${badge.name} has been ${!badge.enabled ? 'enabled' : 'disabled'}.`,
      });
      loadBadges();
    } catch (error: any) {
      toast({
        title: "Failed to update badge",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleImageChange = async (url: string) => {
    setFormData(prev => ({ ...prev, imageUrl: url }));
  };

  return (
    <div className="flex-1 p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <Award className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Badges</h1>
            <p className="text-slate-600">Manage achievement badges</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link href="/badges/order">
            <Button variant="outline" className="flex items-center space-x-2">
              <ArrowUpDown className="w-4 h-4" />
              <span>Order Badges</span>
            </Button>
          </Link>
          <Button onClick={handleAddBadge} className="flex items-center space-x-2" data-testid="button-add-badge">
            <Plus className="w-4 h-4" />
            <span>Add Badge</span>
          </Button>
        </div>
      </div>

      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Award className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Total Badges</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="text-total-badges">{badges.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Award className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-600">Enabled</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="text-enabled-badges">
                  {badges.filter(b => b.enabled).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Badges List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading badges...</p>
        </div>
      ) : badges.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Award className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">No badges found</p>
            <Button onClick={handleAddBadge} data-testid="button-add-first-badge">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Badge
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Image</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Enabled</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {badges.map((badge) => (
                    <tr key={badge.id} className="hover:bg-slate-50" data-testid={`row-badge-${badge.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {badge.imageUrl ? (
                          <img 
                            src={badge.imageUrl} 
                            alt={badge.name}
                            className="w-12 h-12 object-cover rounded"
                            data-testid={`img-badge-${badge.id}`}
                          />
                        ) : (
                          <div 
                            className={`w-12 h-12 rounded flex items-center justify-center ${getColourClasses(badge.colour)}`}
                            data-testid={`icon-badge-${badge.id}`}
                          >
                            <Award className="w-6 h-6" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-slate-900" data-testid={`text-badge-name-${badge.id}`}>{badge.name}</div>
                        <div className="text-sm text-slate-500">{badge.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize" data-testid={`text-badge-type-${badge.id}`}>
                          {badge.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Switch
                          checked={badge.enabled}
                          onCheckedChange={() => handleToggleEnabled(badge)}
                          data-testid={`switch-badge-enabled-${badge.id}`}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditBadge(badge)}
                          className="mr-2"
                          data-testid={`button-edit-badge-${badge.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteBadge(badge)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`button-delete-badge-${badge.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingBadge ? 'Edit Badge' : 'Add New Badge'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter badge name"
                data-testid="input-badge-name"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter badge description"
                rows={3}
                data-testid="input-badge-description"
              />
            </div>

            {/* Message on Earning */}
            <div>
              <Label htmlFor="messageOnEarning">Message on Earning *</Label>
              <Textarea
                id="messageOnEarning"
                value={formData.messageOnEarning}
                onChange={(e) => setFormData(prev => ({ ...prev, messageOnEarning: e.target.value }))}
                placeholder="Enter the message shown to users when they earn this badge"
                rows={3}
                data-testid="input-badge-message-on-earning"
              />
            </div>

            {/* Key */}
            <div>
              <Label htmlFor="key">Key * (unique identifier)</Label>
              <Input
                id="key"
                value={formData.key}
                onChange={(e) => {
                  const key = e.target.value.toLowerCase();
                  setFormData(prev => ({ ...prev, key }));
                  setKeyError('');
                }}
                onBlur={() => validateKey(formData.key)}
                placeholder="e.g., first-program-complete"
                className={keyError ? 'border-red-500' : ''}
                data-testid="input-badge-key"
              />
              {keyError && <p className="text-sm text-red-600 mt-1">{keyError}</p>}
              <p className="text-sm text-slate-500 mt-1">Use lowercase letters, numbers, hyphens, and underscores only</p>
            </div>

            {/* Colour */}
            <div>
              <Label htmlFor="colour">Colour</Label>
              <Select
                value={formData.colour}
                onValueChange={(value) => setFormData(prev => ({ ...prev, colour: value as BadgeData['colour'] }))}
              >
                <SelectTrigger data-testid="select-badge-colour">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COLOUR_OPTIONS.map((colour) => (
                    <SelectItem key={colour} value={colour} data-testid={`option-colour-${colour}`}>
                      <span className="capitalize">{colour}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type */}
            <div>
              <Label htmlFor="type">Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, type: value as BadgeData['type'] }))}
              >
                <SelectTrigger data-testid="select-badge-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type} value={type} data-testid={`option-type-${type}`}>
                      <span className="capitalize">{type}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Image Upload */}
            <div>
              <Label>Badge Image (optional)</Label>
              <ImageUpload
                value={formData.imageUrl}
                onChange={handleImageChange}
                label="Badge Image"
                required={false}
                badgeKey={formData.key}
                uploadType="badge"
              />
              <p className="text-sm text-slate-500 mt-1">If no image is uploaded, a colored badge icon will be displayed</p>
            </div>

            {/* Enabled Toggle */}
            <div className="flex items-center space-x-2">
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enabled: checked }))}
                data-testid="switch-badge-enabled-form"
              />
              <Label htmlFor="enabled" className="cursor-pointer">Enabled</Label>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                disabled={saving}
                data-testid="button-cancel-badge"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveBadge}
                disabled={saving}
                data-testid="button-save-badge"
              >
                {saving ? 'Saving...' : (editingBadge ? 'Update Badge' : 'Create Badge')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
